import {
  dictionaryDefaultColour,
  dictionaryTermTypes,
  EMPTY_CATEGORY,
  EMPTY_TERM,
  entryTypes,
} from "constants/dictionary";
import { format } from "date-fns";

export const NEW_ID_PREFIX = "NEW_ITEM";

export const FORMATTED_DATE_PATTERN = "dd MMM, yyyy";

export const formatDictionaryDate = (isoDate) => {
  if (!isoDate) {
    return null;
  }
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime())
    ? isoDate
    : format(date, FORMATTED_DATE_PATTERN);
};

export const createEmptyCategory = () => {
  return {
    id: -1,
    type: entryTypes.CATEGORY,
    isExpanded: true,
    name: "",
    description: "",
    terms: [],
    newlyCreated: true,
    colour: dictionaryDefaultColour,
    error: EMPTY_CATEGORY,
  };
};

export const createEmptyTerm = (categoryColour) => {
  return {
    id: -1,
    categoryId: -1,
    type: entryTypes.TERM,
    name: "",
    caseSensitive: false,
    description: "",
    dictionaryTermType: dictionaryTermTypes.STRING,
    newlyCreated: true,
    colour: categoryColour || dictionaryDefaultColour,
    error: EMPTY_TERM,
  };
};

export const formatRequest = (dictionary) => {
  return {
    ...dictionary,
    categories: dictionary.categories.map((category) => ({
      name: category.name,
      colour: category.colour,
      description: category.description.trim(),
      terms: category.terms.map((term) => ({
        name: term.name,
        colour: term.colour,
        description: term.description.trim(),
        caseSensitive: term.caseSensitive,
        dictionaryTermType: term.dictionaryTermType,
      })),
    })),
  };
};

// @TODO - May not need this anymore, leaving a note here.
export const formatResponse = (dictionary) => {
  return {
    ...dictionary,
    categories: dictionary.categories.map((category, categoryIndex) => ({
      ...category,
      id: `category-${categoryIndex}`,
      type: entryTypes.CATEGORY,
      isExpanded: category.isExpanded || false,
      terms:
        category.terms !== null
          ? category.terms.map((term) => ({
              ...term,
              categoryId: `category-${categoryIndex}`,
              type: entryTypes.TERM,
            }))
          : [],
    })),
  };
};

export const displayName = (name) => {
  if (name.length <= 57) {
    return name;
  }
  const newName = name.substring(name.length - 57);
  const position = newName.indexOf("/");
  return `...${newName.substring(position)}`;
};

export const sortObjectsByTextField = (objects, field, ascending = true) => {
  return objects.sort((a, b) => {
    const aField = a[field] ? a[field].toUpperCase() : "";
    const bField = b[field] ? b[field].toUpperCase() : "";
    if (aField < bField) {
      return ascending ? -1 : 1;
    }
    if (aField > bField) {
      return ascending ? 1 : -1;
    }
    return 0;
  });
};

export const sortObjectsByDateField = (objects, field, ascending = true) => {
  // Sort from newest date to oldest
  return objects.sort((a, b) => {
    const aDate = a[field] ? a[field] : 0;
    const bDate = b[field] ? b[field] : 0;
    return ascending
      ? new Date(bDate) - new Date(aDate)
      : new Date(aDate) - new Date(bDate);
  });
};

export const sortObjectsByBooleanField = (objects, field, ascending = true) => {
  return objects.sort((a, b) =>
    ascending ? b[field] - a[field] : a[field] - b[field]
  );
};

export const sortDictionariesByFoldersAndDocuments = (
  dictionaries,
  ascending = true
) => {
  return dictionaries.sort((a, b) => {
    if (ascending) {
      // If a has more folders then b, then a should be before b
      if (a.usedBy.folders.length > b.usedBy.folders.length) {
        return -1;
      }

      // If a has less folders then b, then a should be after b
      if (a.usedBy.folders.length < b.usedBy.folders.length) {
        return 1;
      }

      // If a has more documents then b, then a should be before b
      if (a.usedBy.documents.length > b.usedBy.documents.length) {
        return -1;
      }

      // If a has less documents then b, then a should be after b
      if (a.usedBy.documents.length < b.usedBy.documents.length) {
        return 1;
      }
    } else {
      // If a has more folders then b, then a should be after b
      if (a.usedBy.folders.length > b.usedBy.folders.length) {
        return 1;
      }

      // If a has less folders then b, then a should be before b
      if (a.usedBy.folders.length < b.usedBy.folders.length) {
        return -1;
      }

      // If a has more documents then b, then a should be after b
      if (a.usedBy.documents.length > b.usedBy.documents.length) {
        return 1;
      }

      // If a has less documents then b, then a should be before b
      if (a.usedBy.documents.length < b.usedBy.documents.length) {
        return -1;
      }
    }

    return 0;
  });
};

// We don't want these characters having any significance in VT Regexes
const SPECIAL_REGEX_CHARS_TO_ESCAPE = "[{}()\\[\\].+*?^$\\\\|]";

const SPECIAL_REGEX_CHARS = "[{}()\\[\\].+*?^$#\\\\|]";
const SPECIAL_WILDCARD_CHARS = "[\\[\\]+*?!#]";

export const hasSpecialWildcardCharacters = (term) =>
  term.match(`.*${SPECIAL_WILDCARD_CHARS}.*`) !== null;

export const hasSpecialRegexCharacters = (term) =>
  term.match(`.*${SPECIAL_REGEX_CHARS}.*`) !== null;

export const escapeSpecialRegexCharacters = (term) =>
  term.replace(new RegExp(`(${SPECIAL_REGEX_CHARS_TO_ESCAPE})`, "g"), "\\$1");

export const isValidRegex = (regex) => {
  try {
    new RegExp(regex);
  } catch {
    return false;
  }
  return true;
};

// Based off https://support.microsoft.com/en-us/office/use-wildcards-in-queries-and-parameters-in-access-ec057a45-78b1-4d16-8c20-242cde582e0b
// https://en.wikipedia.org/wiki/Regular_expression
// Please see test
export const vtRegexToRegex = (term) => {
  /*
      This is a wildcard expression, general regular expression characters not supported so these are
      escaped
    */
  term = escapeSpecialRegexCharacters(term);
  /*
      Out of those escaped character sequence plug out the ones that are VT Regex related and replace
      with actual regular expression syntax
    */
  term = term.replace(new RegExp("\\\\\\*", "g"), "\\w*");
  term = term.replace(new RegExp("\\\\\\?", "g"), "\\w");
  term = term.replace(new RegExp("\\\\\\[!", "g"), "[^");
  term = term.replace(new RegExp("\\\\\\[", "g"), "[");
  term = term.replace(new RegExp("\\\\\\]", "g"), "]");
  term = term.replace(new RegExp("#\\\\\\+", "g"), "(\\d)+");
  term = term.replace(new RegExp("#", "g"), "\\d");
  /*
      Our VT Regular Expressions are matched on word boundaries so things like spaces and tabs but
      complete patterns of word characters
    */
  term = `(\\b|^)${term}`;
  if (new RegExp(/.*\b$/).test(term)) { // NOSONAR javascript:S5852 - * is a single flat greedy quantifier with no nested structure; \b is a zero-width assertion checked once per backtrack step — worst-case backtracking is O(n) linear.
    // Expression ends in normal word character
    term += "\\b";
  } else {
    // expression ending in word characters?
    const endWildcardWordCharacters =
      term.endsWith("\\w*") || term.endsWith("(\\d)+");

    if (endWildcardWordCharacters) {
      // Word Character Iteration Patterns
      term += "\\b";
    } else {
      // Expression may have non-word characters e.g. C++
      // We would like C+++ to fail and C++. to pass but we don't have whitespace or punctuation boundaries just non-word character boundaries.
      term += "(\\b|\\B)";
      // Could do something like this term = term + "($|[\\\\p{Punct}\\\\p{Space}])"; and then when matching remove the punctuation and trim the matches...
    }
  }
  return term;
};

export const vtRegexToMatches = (term, sentence) => {
  const regex = vtRegexToRegex(term);
  const results = [];
  const p = new RegExp(regex, "g");
  const matches = [...sentence.matchAll(p)];
  matches.forEach((m) => {
    if (m[0].length > 0) {
      results.push(m[0]);
    }
  });
  return results;
};

export class WildcardExpressionParser {
  constructor(s) {
    this.s = s;
    this.index = 0;
    this.dictionaryTermType = dictionaryTermTypes.STRING;
    this.rangeCharacterPattern = new RegExp(/[-!\w]/);
  }

  wep() {
    if (!this.factor()) {
      return false;
    }
    return this.wepList();
  }

  wepList() {
    while (!this.endTokenStream()) {
      if (!this.factor()) {
        return false;
      }
    }
    return true;
  }

  factor() {
    const c = this.nextToken();
    if (c === "#") {
      this.dictionaryTermType = dictionaryTermTypes.WILDCARD_EXPRESSION;
      if (!this.endTokenStream() && this.s.charAt(this.index) === "+") {
        this.nextToken();
      }
    } else if (c === "*" || c === "?") {
      this.dictionaryTermType = dictionaryTermTypes.WILDCARD_EXPRESSION;
    } else if (c === "[") {
      this.dictionaryTermType = dictionaryTermTypes.WILDCARD_EXPRESSION;
      if (!this.rangeSequence()) {
        return false;
      }
      if (
        !this.endTokenStream() &&
        (this.s.charAt(this.index) === "*" || this.s.charAt(this.index) === "?")
      ) {
        this.nextToken();
      }
    }
    return true;
  }

  endTokenStream() {
    return this.index >= this.s.length;
  }

  nextToken() {
    const c = this.s.charAt(this.index);
    this.index++;
    return c;
  }

  rangeSequence() {
    if (this.endTokenStream()) {
      return false;
    }
    let c = this.nextToken();
    if (this.endTokenStream()) {
      return false;
    }
    let p = "";
    while (!this.endTokenStream() && c !== "]") {
      if (!this.rangeCharacterPattern.test(c)) {
        return false;
      }
      p = c;
      c = this.nextToken();
      if (c === "]" && p === "\\") {
        if (this.endTokenStream()) {
          return false;
        }
        c = this.nextToken();
      }
    }
    return c === "]";
  }

  isWildcard() {
    return this.dictionaryTermType !== dictionaryTermTypes.STRING;
  }
}

export const isWildcardExpression = (term) => {
  const wep = new WildcardExpressionParser(term);
  if (!wep.wep()) {
    return false;
  }
  return wep.isWildcard();
};

export const hexToRGBA = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const hasWildcardSymbols = (term) => {
  const symbols = ["*", "?", "[", "]", "!", "-", "#"];
  const { name } = term;

  for (let i = 0; i < symbols.length; i++) {
    if (name.includes(symbols[i])) {
      return true;
    }
  }

  return false;
};

export const areTermsSame = (term1, term2) => {
  if (
    term1.name.toUpperCase() !== term2.name.toUpperCase() ||
    (term1.dictionaryTermType !== term2.dictionaryTermType &&
      hasWildcardSymbols(term2))
  ) {
    return false;
  }
  if (
    term1.dictionaryTermType !== term2.dictionaryTermType &&
    hasWildcardSymbols(term2)
  ) {
    return false;
  }
  if (term1.caseSensitive && term2.caseSensitive) {
    return term1.name === term2.name;
  }
  if (!term1.caseSensitive && !term2.caseSensitive) {
    return term1.name.toUpperCase() === term2.name.toUpperCase();
  }
  return false;
};

export const isTermUnique = (targetTerm, terms) => {
  const { id: targetId } = targetTerm;
  return (
    terms.find((term) => {
      if (term.id === targetId) {
        return false;
      }
      return areTermsSame(term, targetTerm);
    }) === undefined
  );
};

export const isCategoryUnique = (category, categories) => {
  for (let i = 0; i < categories.length; i += 1) {
    const currentCategory = categories[i];

    if (
      currentCategory !== category &&
      currentCategory.name === category.name
    ) {
      return false;
    }
  }

  return true;
};

// Can't have same colour as same term from other category
export const isTermColourValid = (term, categories) => {
  const { colour, categoryId } = term;

  if (colour === dictionaryDefaultColour) {
    return true;
  }

  for (let i = 0; i < categories.length; i++) {
    const { id, terms } = categories[i];

    if (categoryId === id) {
      continue;
    }

    for (let j = 0; j < terms.length; j++) {
      const categoryTerm = terms[j];

      if (!areTermsSame(term, categoryTerm)) {
        continue;
      }

      if (colour === categoryTerm.colour) {
        return false;
      }
    }
  }

  return true;
};

export const getErrorMessage = (errors) => {
  if (errors.length === 0) {
    return "";
  }
  if (errors.length === 1) {
    return errors[0];
  }

  let message = "";
  errors.forEach((error, index) => {
    message += `${index + 1}. ${error}\n`;
  });

  return message;
};

export const isTermRow = (row) => {
  return row?.childrenOf !== undefined;
};
